import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ChevronRightIcon } from '@zep-us/zus-icons';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import '@/shared/features/phone-verification/tracking/events.quiz-trial';
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useActivateVoucher, useGetMyVouchers } from '@/api/gen/vouchers/vouchers.gen';
import { CouponFillIcon } from '@/icons';
import { handleVoucherActivationResolution } from '@/shared/features/payment-resolution/utils';
import { normalizeTrialSource } from '@/shared/features/phone-verification/tracking/trialTracking';
import { refreshVoucherActivationState } from '@/shared/plan/refreshVoucherActivationState';
import { showVoucherAppliedToast, showVoucherStoredToast } from '@/shared/plan/voucherToasts';
import { alertDialog, Button, DialogBody, DialogFooter, DialogTitle, ResponsiveDialog } from '@/ui_v3';
import { getTrialVoucherContext } from '../trialVoucherContext';
export const TrialVoucherRewardModal = ({
  onDismiss,
  onNavigate,
  voucherHashId
}: {
  onDismiss: () => Promise<void>;
  onNavigate: () => void;
  voucherHashId: string;
}) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    t
  } = useTranslation(['common']);
  const {
    mutateAsync: activateVoucher,
    isPending: isActivationPending
  } = useActivateVoucher();
  const {
    data: vouchersResponse,
    isError: isVouchersError,
    isFetching: isVouchersFetching,
    refetch: refetchVouchers
  } = useGetMyVouchers({
    usageStatus: 'UNUSED'
  }, {
    query: {
      throwOnError: false
    }
  });
  const issuedVoucher = vouchersResponse?.data.items.find(voucher => voucher.hashId === voucherHashId);
  const trialVoucherContext = useMemo(() => getTrialVoucherContext(issuedVoucher?.systemCampaignType), [issuedVoucher?.systemCampaignType]);
  const missingVoucherRetryRef = useRef<string>();
  const autoDismissedRef = useRef<string>();
  const [isMissingVoucherSettled, setIsMissingVoucherSettled] = useState(false);
  const isVoucherResolving = !isVouchersError && (!vouchersResponse || isVouchersFetching);
  const canUseNow = Boolean(!isVoucherResolving && trialVoucherContext && vouchersResponse?.data.canUseVouchers && issuedVoucher?.canUseNow);
  const isPhoneVerificationVoucher = trialVoucherContext?.voucher_type === 'phone_verification';
  const diagnosticTags: Record<string, string> = isPhoneVerificationVoucher ? {
    feature: 'phone-verification'
  } : {
    feature: 'voucher',
    voucher_type: trialVoucherContext?.voucher_type ?? 'unknown'
  };
  const diagnosticSource = isPhoneVerificationVoucher ? 'phone_verification' : 'trial_voucher_modal';
  const from = router.query.from as 'signup' | 'profile_edit' | 'banner_modal' || undefined;
  const trialSource = normalizeTrialSource(router.query.trialSource ?? from);
  useEffect(() => {
    if (!vouchersResponse || issuedVoucher || isVouchersFetching || missingVoucherRetryRef.current === voucherHashId) return;
    missingVoucherRetryRef.current = voucherHashId;
    // 재조회가 "끝났다"는 사실을 따로 남긴다. 아래 자동 닫기는 재조회를 시작했는지가
    // 아니라 끝났는지를 봐야 한다 — 시작만 보고 판정하면 같은 커밋에서 곧바로 닫혀
    // 재조회 결과를 못 본다.
    void Promise.resolve(refetchVouchers()).finally(() => setIsMissingVoucherSettled(true));
  }, [issuedVoucher, isVouchersFetching, refetchVouchers, voucherHashId, vouchersResponse]);

  // 이 창은 URL 신호(issuedVoucherId) 하나만 믿고 열린다. 신호가 가리키는 이용권을
  // 끝내 찾지 못하거나 지원하지 않는 캠페인이면 보여 줄 것이 없으므로 스스로 닫는다.
  // 그러지 않으면 조작된 URL 에도, 미지원 캠페인에도 "이용권을 받았어요" 문구만 남고
  // 쓸 수 있는 버튼이 없는 창이 남는다.
  //
  // 이용권을 못 찾은 경우는 위 재조회를 소진한 뒤에만 판정한다 — 가입 직후에는 방금
  // 발급된 이용권이 목록에 아직 안 잡힐 수 있고, 그 지연이 곧 재조회의 존재 이유다.
  //
  // 조회 자체가 실패한 경우(isVouchersError)는 닫지 않는다. 이용권이 없다고 확인한 게
  // 아니라 확인을 못 한 것이라, 방금 받은 이용권의 안내를 통신 오류로 지워 버리게 된다.
  // 그 상태의 폴백(보관함·닫기)은 아래 렌더가 이미 갖고 있다.
  useEffect(() => {
    if (isVoucherResolving || isVouchersError) return;
    if (issuedVoucher && trialVoucherContext) return;
    if (!issuedVoucher && !isMissingVoucherSettled) return;
    if (autoDismissedRef.current === voucherHashId) return;
    autoDismissedRef.current = voucherHashId;
    void onDismiss();
  }, [isMissingVoucherSettled, isVoucherResolving, isVouchersError, issuedVoucher, onDismiss, trialVoucherContext, voucherHashId]);
  useEffect(() => {
    if (!trialVoucherContext) return;
    trackEvent('quiz_trial_voucher_modal_view', {
      voucher_id: voucherHashId,
      trial_source: trialSource,
      voucher_type: trialVoucherContext.voucher_type,
      trial_region: trialVoucherContext.trial_region,
      trial_type: trialVoucherContext.trial_type
    });
  }, [trialSource, trialVoucherContext, voucherHashId]);

  // 라우팅하지 않는다. 이 모달은 이미 복귀한 화면 위에서 열리므로(PRD 솔루션 1·2)
  // 여기서 또 이동하면 사용자를 원래 작업에서 떼어놓게 된다.
  //
  // 시안 28913:51379 의 「다음에」와 28913:51481 의 「닫기」가 같은 동작이다 —
  // 상태에 따라 라벨만 다를 뿐, 둘 다 "이용권을 안 쓰고 나간다"는 하나의 행동이다.
  // 창만 조용히 닫으면 방금 받은 이용권이 어디로 갔는지 알 수 없으므로
  // 보관됐다는 사실과 갈 곳을 토스트로 남긴다(시안 28913:51596).
  const handleDismissWithoutUsing = async () => {
    if (trialVoucherContext) {
      trackEvent('quiz_trial_voucher_action_click', {
        voucher_id: voucherHashId,
        trial_source: trialSource,
        action: 'use_later',
        voucher_type: trialVoucherContext.voucher_type,
        trial_region: trialVoucherContext.trial_region,
        trial_type: trialVoucherContext.trial_type
      });
    }
    // 적용 경로와 같은 순서다 — URL 신호를 정리한 뒤 토스트를 띄운다.
    // 정리 라우팅의 전역 오버레이 정리가 토스트를 닫지 않도록 순서를 보장한다.
    await onDismiss();
    showVoucherStoredToast(() => void router.push('/profile/plan'), {
      title: t('common:voucher.stored.title'),
      actionLabel: t('common:voucher.storage.title'),
      closeButtonLabel: t('common:global.close.button')
    });
  };
  const handleGoToStorage = () => {
    if (isPhoneVerificationVoucher) {
      trackEvent('voucher_storage_click', {
        location: 'phone_verification',
        from
      });
    }
    if (trialVoucherContext) {
      trackEvent('quiz_trial_voucher_action_click', {
        voucher_id: voucherHashId,
        trial_source: trialSource,
        action: 'open_storage',
        voucher_type: trialVoucherContext.voucher_type,
        trial_region: trialVoucherContext.trial_region,
        trial_type: trialVoucherContext.trial_type
      });
    }
    onNavigate();
    void router.push('/profile/plan');
  };
  const handleUseNow = async () => {
    if (trialVoucherContext) {
      trackEvent('quiz_trial_voucher_action_click', {
        voucher_id: voucherHashId,
        trial_source: trialSource,
        action: 'use_now',
        voucher_type: trialVoucherContext.voucher_type,
        trial_region: trialVoucherContext.trial_region,
        trial_type: trialVoucherContext.trial_type
      });
    }
    try {
      const resolution = await activateVoucher({
        voucherHashId
      });
      const activationState = await handleVoucherActivationResolution({
        resolution: resolution.data,
        voucherHashId,
        trialTrackingContext: trialVoucherContext ? {
          trial_source: trialSource,
          voucher_type: trialVoucherContext.voucher_type,
          trial_region: trialVoucherContext.trial_region,
          trial_type: trialVoucherContext.trial_type
        } : undefined,
        navigate: router.push
      });
      if (activationState === 'processing') return;
      if (trialVoucherContext) {
        trackEvent('quiz_trial_voucher_activation_success', {
          voucher_id: voucherHashId,
          trial_source: trialSource,
          voucher_type: trialVoucherContext.voucher_type,
          trial_region: trialVoucherContext.trial_region,
          trial_type: trialVoucherContext.trial_type
        });
      }
      const refreshResults = await refreshVoucherActivationState(queryClient);
      refreshResults.forEach(result => {
        if (result.status === 'rejected') {
          logger.captureError(result.reason, {
            tags: {
              ...diagnosticTags,
              action: 'refresh_after_activation'
            },
            extra: {
              voucherHashId,
              source: diagnosticSource
            }
          });
        }
      });
      await onDismiss();
      // 헤더에서 쓸 때와 같은 토스트다(시안 28913:51588 · 51593). 적용 사실만 알리고
      // 상세는 플랜 관리로 넘긴다 — 이 경로만 조용하면 같은 행동의 결과가 달라 보인다.
      // URL 신호를 정리한 뒤 토스트를 띄운다. 정리 라우팅의 전역 오버레이 정리가
      // 토스트를 닫지 않도록 순서를 보장한다.
      showVoucherAppliedToast(() => void router.push('/profile/plan'), {
        title: t('common:voucher.applied.title'),
        description: t('common:voucher.applied.description'),
        actionLabel: t('common:planManage'),
        closeButtonLabel: t('common:global.close.button')
      });
    } catch (error) {
      if (trialVoucherContext) {
        trackEvent('quiz_trial_voucher_activation_fail', {
          voucher_id: voucherHashId,
          trial_source: trialSource,
          reason: 'activation_failed',
          voucher_type: trialVoucherContext.voucher_type,
          trial_region: trialVoucherContext.trial_region,
          trial_type: trialVoucherContext.trial_type
        });
      }
      logger.captureError(error, {
        tags: {
          ...diagnosticTags,
          action: 'activateVoucher'
        }
      });
      await alertDialog.openAsync({
        header: {
          title: t('common:voucher.activateFailed.title')
        },
        body: t('common:voucher.commonError.body')
      });
    }
  };
  return <ResponsiveDialog onClose={() => void onDismiss()} size="lg" shouldCloseOnDimClick={false} data-sentry-element="ResponsiveDialog" data-sentry-component="TrialVoucherRewardModal" data-sentry-source-file="TrialVoucherRewardModal.tsx">
      <DialogBody data-sentry-element="DialogBody" data-sentry-source-file="TrialVoucherRewardModal.tsx">
        <main className="flex h-full flex-col gap-md overflow-y-auto p-lg px-md mobile-lg:flex-row mobile-lg:px-lg">
          <div className="flex flex-col gap-sm px-[6px] mobile-lg:gap-xs mobile-lg:p-md mobile-lg:pr-0">
            <div className="flex items-center gap-sm mobile-lg:flex-col mobile-lg:items-start mobile-lg:gap-lg">
              <div className="flex size-[42px] items-center justify-center rounded-sm bg-gradient-to-r from-violet-400 to-blue-400">
                <CouponFillIcon size="lg" fill={ICON_COLOR.inverse} data-sentry-element="CouponFillIcon" data-sentry-source-file="TrialVoucherRewardModal.tsx" />
              </div>
              {/*
                Radix 는 DialogContent 마다 DialogTitle 을 요구한다(없으면 스크린리더가
                이 창이 무엇인지 읽어 줄 수 없고 콘솔 경고가 난다).
                이미 보이는 제목이 있으므로 sr-only 제목을 따로 두지 않고 asChild 로
                이 h1 자체를 제목으로 지정한다 — 문구가 두 벌이 되면 갈라진다.
               */}
              <DialogTitle asChild data-sentry-element="DialogTitle" data-sentry-source-file="TrialVoucherRewardModal.tsx">
                <h1 className="text-heading-xl-extrabold">
                  <Trans t={t} i18nKey="phoneVerify.rewardModal.title" components={{
                  br: <>
                          <span className="mobile-lg:hidden">&nbsp;</span>
                          <br className="hidden mobile-lg:block" />
                        </>
                }} data-sentry-element="Trans" data-sentry-source-file="TrialVoucherRewardModal.tsx" />
                </h1>
              </DialogTitle>
            </div>
            <p className="whitespace-pre-line text-body-lg-medium text-default opacity-90">
              <Trans t={t} i18nKey="phoneVerify.rewardModal.planBenefit" components={{
              Strong: <strong className="text-body-lg-bold" />
            }} data-sentry-element="Trans" data-sentry-source-file="TrialVoucherRewardModal.tsx" />
            </p>
            <p className="text-body-lg-medium">
              {t('phoneVerify.rewardModal.description2')}
            </p>
          </div>
          <div className="min-h-[375px] w-full shrink-0 mobile-lg:min-h-[500px] mobile-lg:max-w-[400px]">
            <img src="/assets/phone-verification-complete.webp" alt="Verification Reward Modal" className="size-full rounded-lg object-cover" />
          </div>
        </main>
      </DialogBody>

      {/* Footer */}
      <DialogFooter className="flex gap-xs border-t border-alternative shadow-lg mobile-lg:pt-md" data-sentry-element="DialogFooter" data-sentry-source-file="TrialVoucherRewardModal.tsx">
        {canUseNow ? <>
            <Button variant="contained" intent="neutral" size="lg" onClick={() => void handleDismissWithoutUsing()} disabled={isActivationPending} className="flex-1">
              {t('common:voucher.useLater')}
            </Button>
            {/*
              시안(28913:51480)은 두 버튼을 1:1 로 나누지 않는다 — 「바로 사용하기」가
              **400px 고정**이고 「다음에」가 남는 폭을 가져간다(720px 기준 264:400).
              주 액션의 크기를 화면 폭에 맡기지 않고 못 박아 둔 것이라, flex-1 로 두면
              둘이 332:332 로 같아져 무엇이 주 액션인지 흐려진다.
               고정폭은 다이얼로그가 실제로 720px 인 mobile-lg 이상에서만 건다
              (`ResponsiveDialog` lg = `w-full mobile-lg:w-[720px]`). 그 아래는 전폭
              드로어라 1:1 을 유지한다. grow 0 / shrink 1 이라 폭이 모자라면 줄어든다.
             */}
            <Button variant="contained" intent="gradient" size="lg" onClick={() => void handleUseNow()} disabled={isActivationPending} className="flex-1 mobile-lg:flex-[0_1_400px]">
              {t('common:voucher.activateNow')}
            </Button>
          </> : <>
            {/*
              지금 쓸 수 없으니 「다음에」가 아니라 「닫기」다 — 미루는 게 아니라
              이 창에서 할 일이 없다는 뜻이라서 라벨이 다르다(시안 28913:51481).
              동작은 「다음에」와 같다: 안 쓰고 나가고, 보관 토스트를 남긴다.
             */}
            <Button variant="contained" intent="neutral" size="lg" onClick={() => void handleDismissWithoutUsing()} disabled={isVoucherResolving || isActivationPending} className="flex-1">
              {t('common:global.close.button')}
            </Button>
            {/* 폭 비율은 canUseNow 상태와 동일하다(시안 264:400). */}
            <Button variant="contained" intent="gradient" size="lg" rightSlot={<ChevronRightIcon size="md" />} onClick={handleGoToStorage} disabled={isVoucherResolving || isActivationPending} className="flex-1 mobile-lg:flex-[0_1_400px]">
              {t('common:voucher.storage.title')}
            </Button>
          </>}
      </DialogFooter>
    </ResponsiveDialog>;
};